import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import { chatApi } from "../../apis/Chat.api";
import { serverUrl } from "../../config/apiConfig";
import { toast } from "react-toastify";

const SocketContext = createContext(null);
export const useSocket = () => useContext(SocketContext);

const SocketProvider = ({ children }) => {
  const user = useSelector((state) => state.store.currentUser);
  const userId = user?._id;

  /* -------------------- STATE -------------------- */
  const [receiverUID, setReceiverUID] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [allMessages, setAllMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  /* -------------------- SOCKET -------------------- */
  const socket = React.useMemo(
    () =>
      io(`${serverUrl}/chat`, {
        autoConnect: false,
        transports: ["websocket"],
        withCredentials: true,
      }),
    []
  );

  /* -------------------- CONNECT -------------------- */
  useEffect(() => {
    if (!userId) return;

    socket.connect();
    socket.emit("joined", { userId });

    return () => {
      socket.disconnect();
    };
  }, [userId, socket]);

  /* -------------------- FETCH CHAT HISTORY -------------------- */
  useEffect(() => {
    if (!userId || !receiverUID || !orderId) return;

    socket.emit("single-user-msgs", {
      senderId: userId,
      receiverId: receiverUID,
      orderId,
    });
  }, [userId, receiverUID, orderId, socket]);

  /* -------------------- RECEIVE MESSAGES -------------------- */
  useEffect(() => {
    const handleAllMessages = (messages) => {
      setAllMessages(messages);
    };

    const handleReceiveMessage = (payload) => {
      setAllMessages((prev) => [
        ...prev,
        {
          sender_id: { _id: payload.senderId },
          receiver_id: { _id: payload.receiverId },
          message: payload.message,
          created_at: payload.timestamp,
          order_id: orderId,
        },
      ]);
    };

    socket.on("user-all-messages", handleAllMessages);
    socket.on("receive-message", handleReceiveMessage);

    return () => {
      socket.off("user-all-messages", handleAllMessages);
      socket.off("receive-message", handleReceiveMessage);
    };
  }, [socket, orderId]);

  /* -------------------- FILE UPLOAD -------------------- */
  const uploadFiles = async (files, senderId, receiverId) => {
    const formData = new FormData();

    files.forEach((file) => formData.append("files", file));
    formData.append("senderId", senderId);
    formData.append("receiverId", receiverId);

    const res = await chatApi.uploadFiles(formData, {
      baseURL: `${serverUrl}/api/v1/`,
      onUploadProgress: (e) =>
        setProgress(Math.round((e.loaded * 100) / e.total)),
    });

    return res.data.filePaths.map((path) => ({
      senderId,
      receiverId,
      filePath: path,
      orderId,
    }));
  };

  /* -------------------- SEND MESSAGE -------------------- */
  const handleMessage = async () => {
    if (!userId || !receiverUID || !orderId) return;

    try {
      if (message.trim()) {
        socket.emit("message", {
          senderId: userId,
          receiverId: receiverUID,
          message,
          orderId,
        });
        setMessage("");
      }

      if (files.length > 0) {
        // Capture files locally and clear state immediately to prevent race conditions (ghost images)
        const filesToUpload = [...files];
        setIsLoading(true);
        setFiles([]);
        setPreview([]);

        const uploadedFiles = await uploadFiles(
          filesToUpload,
          userId,
          receiverUID
        );

        uploadedFiles.forEach((file) => {
          socket.emit("file-upload", file);
        });
      }
    } catch (error) {
      console.error("Chat send error:", error);
      toast.error(error.response?.data?.message || "Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------- CONTEXT -------------------- */
  return (
    <SocketContext.Provider
      value={{
        socket,
        message,
        setMessage,
        handleMessage,
        receiverUID,
        setReceiverUID,
        orderId,
        setOrderId,
        allMessages,
        files,
        setFiles,
        preview,
        setPreview,
        isImageModalOpen,
        setIsImageModalOpen,
        isLoading,
        progress,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;
